import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Sparkles, Share2, Copy } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";

/**
 * KidsHighlightShare — public read-only card for a parent's weekly
 * highlight reel. Accessed via token-based share link. No auth needed.
 * Designed to look great as a link preview for friends/family.
 */
export default function KidsHighlightShare() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/kids-public/highlights/share/${token}`)
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [token]);

  const copy = async () => {
    try { await navigator.clipboard.writeText(window.location.href); toast.success("Share link copied!"); }
    catch { toast.error("Couldn't copy"); }
  };

  if (loading) return <div className="pt-32 text-white/60 text-center">Loading…</div>;
  if (!data) return (
    <div className="pt-24 px-6 text-center text-white">
      <div className="text-4xl">🤷</div>
      <div className="mt-2">Share link not found — it may have been revoked.</div>
      <Link to="/kids" className="inline-block mt-4 bg-yellow-300 text-black font-bold px-5 py-2 rounded-full">Visit Kids</Link>
    </div>
  );

  return (
    <div data-testid="kids-highlights-share-page" className="pb-20 px-3 sm:px-6 md:px-10 max-w-3xl mx-auto text-white">
      <div className="mt-3 relative overflow-hidden rounded-3xl p-6 sm:p-10 bg-gradient-to-br from-pink-400 via-fuchsia-500 to-sky-500 shadow-2xl ring-4 ring-white/25">
        <img src="/kids-learn-play-logo.png" alt="" className="absolute -top-6 -right-6 w-48 opacity-30 pointer-events-none" />
        <div className="relative">
          <div className="inline-flex items-center gap-1 bg-white/20 text-white text-[10px] uppercase tracking-[0.25em] font-black px-2 py-0.5 rounded-full backdrop-blur">
            <Sparkles className="w-3 h-3" /> Weekly Highlight Reel
          </div>
          <h1 className="font-display font-black text-4xl md:text-6xl tracking-tighter drop-shadow mt-2">{data.parent_name}'s kids,<br/>this week.</h1>
          <p className="text-white/90 text-sm mt-2">
            {data.totals?.total_minutes ?? 0} total minutes · {data.totals?.total_drawings ?? 0} drawings shared · {data.profiles?.length ?? 0} kid{data.profiles?.length === 1 ? "" : "s"}
          </p>
        </div>
      </div>

      <div data-testid="share-grid" className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
        {(data.profiles || []).map((h) => (
          <div
            key={h.profile_id}
            data-testid={`share-highlight-${h.profile_id}`}
            className="bg-gradient-to-br from-white/10 to-white/5 border border-white/15 rounded-2xl p-5"
          >
            <div className="flex items-center gap-3">
              <div className="text-4xl">{h.avatar_emoji || "⭐"}</div>
              <div>
                <div className="text-white font-display font-black text-xl">{h.name}</div>
                <div className="text-white/70 text-sm inline-flex items-center gap-1">
                  <span className="text-2xl align-middle">{h.medal}</span>
                  {h.days_active}/7 days active
                </div>
              </div>
            </div>
            <div className="mt-4 grid grid-cols-3 gap-2 text-center">
              <div><div className="text-2xl font-black">{h.week_minutes}</div><div className="text-xs text-white/60">minutes</div></div>
              <div><div className="text-2xl font-black">{h.drawings_shared}</div><div className="text-xs text-white/60">drawings</div></div>
              <div><div className="text-2xl font-black capitalize">{(h.top_activity || "-").slice(0, 6)}</div><div className="text-xs text-white/60">top</div></div>
            </div>
            <div className="mt-3 h-2 bg-white/10 rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-pink-400 to-fuchsia-500" style={{ width: `${Math.min(100, (h.week_minutes / Math.max(h.weekly_limit_min, 1)) * 100)}%` }} />
            </div>
          </div>
        ))}
      </div>

      <div className="mt-8 flex flex-wrap items-center gap-2 justify-center">
        <button data-testid="share-copy" onClick={copy} className="bg-gradient-to-r from-pink-500 to-fuchsia-500 text-white font-black px-5 py-2.5 rounded-full inline-flex items-center gap-2"><Copy className="w-4 h-4" /> Copy share link</button>
        <Link to="/kids" className="bg-white/10 hover:bg-white/20 text-white font-bold px-5 py-2.5 rounded-full inline-flex items-center gap-2"><Share2 className="w-4 h-4" /> Visit Tap IN Kids</Link>
      </div>
    </div>
  );
}
